import { usePage } from "@inertiajs/react"
import type { ParseKeys } from "i18next"
import { Search } from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { PageChrome, type PageChromeProps } from "@/components/page-chrome"
import type { WorkspaceKey } from "@/components/workspace/page-chrome"

import {
  searchPageCrumbs,
  searchPageEntry,
  searchPageNamespaces,
} from "./search-pages"
import {
  resolveSearchReturn,
  useCurrentSearchRecord,
} from "./search-return-navigation"

export type SearchPageChromeProps = Omit<
  PageChromeProps,
  "icon" | "title" | "crumbs"
>

// The search domain's chrome. The page is found by its Inertia component
// name in searchPages, and raises when it is not registered. A results page
// is the Search root. Any other page's trail starts at Search, then follows
// the return record the page was reached with, or its canonical parents.
// A record page passes its record's name as identity, which a search link
// followed from it records as its hop. Everything else passes through.
export function SearchPageChrome({
  identity,
  ...chrome
}: SearchPageChromeProps) {
  const { component: page, url } = usePage()
  const entry = searchPageEntry(page)
  const { t } = useTranslation(searchPageNamespaces())
  const translate = (key: WorkspaceKey) => t(key as ParseKeys)
  // The record is read from the URL, so it is read again on a visit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchReturn = useMemo(() => resolveSearchReturn(), [url])
  useCurrentSearchRecord(page, identity?.name)

  return (
    <PageChrome
      {...chrome}
      identity={identity}
      icon={entry.root ? Search : entry.icon}
      title={translate(entry.root ? "nav:search" : entry.title)}
      crumbs={searchPageCrumbs(page, { searchReturn, t: translate })}
    />
  )
}
